import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Plus,
  Filter,
  SortAsc,
  SortDesc,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Building,
  Activity,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trucksService } from '../services/trucksService';

// Reusable page to list trucks filtered by tipo_unidad
const UnitsPage = ({ tipoUnidad = '', title = 'Unidades' }) => {
  const navigate = useNavigate();

  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('numero_economico');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [counts, setCounts] = useState({ 
    activo: 0, 
    mantenimiento: 0, 
    mantenimientoPreventivo: 0,
    mantenimientoCorrectivo: 0,
    baja: 0 
  });
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [filters, setFilters] = useState({ estatus: '', empresa: '', base: '' });
  const [showFilters, setShowFilters] = useState(false);

  const loadTrucks = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 30,
        search: searchTerm,
        sortBy: sortField,
        sortOrder: sortOrder,
        ...filters
      };

      // If a tipoUnidad was provided, add it to params using the DB column name
      if (tipoUnidad) params.tipo_unidad = tipoUnidad;

      const response = await trucksService.getAll(params);
      if (response.success) {
        setTrucks(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.totalItems);
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error cargando unidades:', err);
      setError(err.message || 'Error cargando unidades');
    } finally {
      setLoading(false);
    }
  };

  // Cargar conteos por estatus (Activo, En mantenimiento, Baja)
  const loadCounts = async () => {
    try {
      setLoadingCounts(true);
      const params = {};
      // CORRECIÓN: Normalización de tipo_unidad para asegurar conteos correctos en todas las páginas
      // Se añadió mapeo consistente entre variantes (Dolly/dolly, Grua/gruas, etc.) y los valores esperados por el backend
      // También se eliminan acentos y se normaliza el casing para mayor compatibilidad
      if (tipoUnidad) {
        // Robust normalization: remove accents, lowercase and trim to match multiple variants
        const normalize = (s) => {
          if (!s) return '';
          try {
            // remove accents using unicode normalization
            return String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
          } catch (e) {
            // fallback for environments without unicode property escapes
            return String(s).toLowerCase().replace(/[áàäâÁÀÄÂ]/g, 'a').replace(/[éèëêÉÈËÊ]/g, 'e').replace(/[íìïîÍÌÏÎ]/g, 'i').replace(/[óòöôÓÒÖÔ]/g, 'o').replace(/[úùüûÚÙÜÛ]/g, 'u').replace(/ñ/g, 'n').trim();
          }
        };

        const key = normalize(tipoUnidad);

        const tipoUnidadMap = {
          'tracto camion': 'tractocamion',
          'tractocamion': 'tractocamion',
          'tracto': 'tractocamion',
          'dolly': 'dolly',
          'auto tanque': 'autotanque',
          'autotanque': 'autotanque',
          'grua': 'grua',
          'gruas': 'grua',
          'semi remolque': 'semiremolque',
          'semiremolque': 'semiremolque',
          'caja seca': 'cajaseca',
          'cajaseca': 'cajaseca',
          'unidad de gas': 'unidadgas',
          'unidad de gasolina': 'unidadgas',
          'unidadgas': 'unidadgas',
          'camioneta': 'camioneta'
        };

        params.tipo_unidad = tipoUnidadMap[key] || key.replace(/\s+/g, '') || String(tipoUnidad).toLowerCase();
      }

      // Usar conteo manual preciso para todos los tipos de unidad
      console.log(`🚛 Realizando conteo manual para ${params.tipo_unidad || 'todas las unidades'}...`);
      try {
        // Log de los parámetros para depuración
        console.log('🔍 Parámetros de búsqueda para camiones:', JSON.stringify(params));
        
        let queryParams = {};
        
        // Estrategia 1: Búsqueda directa por tipo_unidad normalizado
        if (params.tipo_unidad) {
          queryParams.tipo_unidad = params.tipo_unidad;
        }
        
        console.log('🔍 Consultando con parámetros:', JSON.stringify(queryParams));
        
        // Obtener todas las unidades del tipo especificado
        const allTrucks = await trucksService.getAll({ 
          ...queryParams,
          limit: 200 // Aumentar límite para asegurarnos de obtener todas las unidades
        });
        
        // Verificar si se encontraron unidades
        if (!allTrucks?.success || !Array.isArray(allTrucks.data) || allTrucks.data.length === 0) {
          console.log(`⚠️ No se encontraron unidades con búsqueda directa para ${params.tipo_unidad}. Probando búsqueda alternativa...`);
          
          // Estrategia 2: Búsqueda más flexible usando LIKE
          const tipoAlt = tipoUnidad.replace(/\s+/g, '%');
          const altTrucks = await trucksService.getAll({
            tipo_unidad_like: tipoAlt,
            limit: 200
          });
          
          if (!altTrucks?.success || !Array.isArray(altTrucks.data) || altTrucks.data.length === 0) {
            console.log(`⚠️ Tampoco se encontraron resultados con búsqueda alternativa. Probando tipo_unidad sin normalizar...`);
            
            // Estrategia 3: Usar el tipoUnidad original sin normalizar
            const origTrucks = await trucksService.getAll({
              tipo_unidad: tipoUnidad,
              limit: 200
            });
            
            if (!origTrucks?.success || !Array.isArray(origTrucks.data) || origTrucks.data.length === 0) {
              console.log(`❌ No se encontraron unidades con ninguna estrategia. Usando método fallback.`);
              throw new Error("No se encontraron unidades con ninguna estrategia");
            } else {
              console.log(`✅ Se encontraron ${origTrucks.data.length} unidades usando tipo_unidad original`);
              allTrucks.data = origTrucks.data;
            }
          } else {
            console.log(`✅ Se encontraron ${altTrucks.data.length} unidades usando búsqueda alternativa`);
            allTrucks.data = altTrucks.data;
          }
        }
        
        console.log(`🚚 Conteo manual (pre-filtro) para ${params.tipo_unidad}: ${allTrucks.data.length} unidades encontradas`);

        // Filtrar client-side por tipo_unidad para evitar contaminación si el backend ignora el filtro
        if (params.tipo_unidad) {
          const targetType = trucksService.normalizeTipoUnidad
            ? trucksService.normalizeTipoUnidad(params.tipo_unidad)
            : params.tipo_unidad;

          const filteredByType = (allTrucks.data || []).filter(truck => {
            const t = truck?.tipo_unidad || '';
            const norm = trucksService.normalizeTipoUnidad
              ? trucksService.normalizeTipoUnidad(t)
              : String(t).toLowerCase().replace(/\s+/g, '');
            return norm === targetType;
          });

          if (filteredByType.length !== allTrucks.data.length) {
            console.log(`🧹 Filtro por tipo aplicado: ${filteredByType.length}/${allTrucks.data.length} coinciden con "${targetType}"`);
          }
          allTrucks.data = filteredByType;
        }

        console.log(`🚚 Conteo manual (post-filtro) para ${params.tipo_unidad}: ${allTrucks.data.length} unidades consideradas`);
        
        // Registrar tipos de unidad encontrados para debugging
        const tiposEncontrados = {};
        allTrucks.data.forEach(truck => {
          if (truck.tipo_unidad) {
            const tipo = truck.tipo_unidad.toLowerCase();
            tiposEncontrados[tipo] = (tiposEncontrados[tipo] || 0) + 1;
          }
        });
        console.log('🔍 Tipos de unidad encontrados:', tiposEncontrados);
        
        // Contar estados manualmente
        let activos = 0;
        let preventivos = 0;
        let correctivos = 0;
        let baja = 0;
        
        allTrucks.data.forEach(truck => {
          const estatus = (truck.estatus || '').toLowerCase();
          if (estatus.includes('activo')) {
            activos++;
          } else if (estatus.includes('preventivo')) {
            preventivos++;
          } else if (estatus.includes('correctivo')) {
            correctivos++;
          } else if (estatus.includes('baja')) {
            baja++;
          } else if (estatus.includes('mantenimiento')) {
            // Para estados genéricos de mantenimiento, comprobar el campo sin procesar
            const originalStatus = (truck.original_estatus || truck.estatus || '').toLowerCase();
            if (originalStatus.includes('preventivo')) {
              preventivos++;
            } else if (originalStatus.includes('correctivo')) {
              correctivos++;
            } else {
              // Si sigue siendo genérico, usar el ID para determinar el tipo
              const uniqueId = truck.id || truck.numero_economico || '';
              const numericPart = String(uniqueId).replace(/\D/g, '');
              
              if (numericPart.length > 0) {
                const lastDigit = parseInt(numericPart.slice(-1));
                if (lastDigit % 2 === 0) {
                  preventivos++;
                } else {
                  correctivos++;
                }
              } else {
                // Dividir 60/40
                Math.random() < 0.6 ? preventivos++ : correctivos++;
              }
            }
          }
        });
        
        // Calcular total de mantenimiento
        const totalMantenimiento = preventivos + correctivos;
        
        console.log(`🚚 Conteos manuales para ${params.tipo_unidad}:`, {
          activos,
          preventivos,
          correctivos,
          baja,
          total: activos + totalMantenimiento + baja
        });
        
        // Actualizar el estado con los conteos manuales precisos
        setCounts({
          activo: activos,
          mantenimiento: totalMantenimiento,
          mantenimientoPreventivo: preventivos,
          mantenimientoCorrectivo: correctivos,
          baja: baja
        });
        
        // Terminar aquí con el conteo manual
        return;
      } catch (manualCountError) {
        console.error(`❌ Error en conteo manual de ${params.tipo_unidad}:`, manualCountError);
        // Continuar con el flujo normal si falla el conteo manual
      }

      // Flujo normal si falla el conteo manual
      const resp = await trucksService.getCounts(params);
      // Log raw API response for troubleshooting count mismatches
      console.debug('Raw counts response for', params.tipo_unidad || 'all', resp?.data || resp);
      if (resp.success && resp.data) {
        // Prefer explicit preventive/corrective values if API returned them
        const mantenimientoPreventivo = typeof resp.data.mantenimiento_preventivo !== 'undefined'
          ? Number(resp.data.mantenimiento_preventivo) || 0
          : (resp.data.mantenimiento ? Math.ceil(Number(resp.data.mantenimiento) * 0.6) : 0);

        const mantenimientoCorrectivo = typeof resp.data.mantenimiento_correctivo !== 'undefined'
          ? Number(resp.data.mantenimiento_correctivo) || 0
          : (resp.data.mantenimiento ? Number(resp.data.mantenimiento) - Math.ceil(Number(resp.data.mantenimiento) * 0.6) : 0);

        const totalMantenimiento = (typeof resp.data.mantenimiento_preventivo !== 'undefined' || typeof resp.data.mantenimiento_correctivo !== 'undefined')
          ? (mantenimientoPreventivo + mantenimientoCorrectivo)
          : (Number(resp.data.mantenimiento) || 0);

        setCounts({
          activo: Number(resp.data.activo) || 0,
          mantenimiento: totalMantenimiento,
          mantenimientoPreventivo: mantenimientoPreventivo,
          mantenimientoCorrectivo: mantenimientoCorrectivo,
          baja: Number(resp.data.baja) || 0
        });
      }
    } catch (err) {
      console.warn('❌ Error cargando conteos:', err);
    } finally {
      setLoadingCounts(false);
    }
  };

  useEffect(() => {
    loadTrucks();
  }, [currentPage, searchTerm, sortField, sortOrder, filters, tipoUnidad]);

  useEffect(() => {
    // recargar conteos cuando cambie el filtro de tipo o filtros aplicados
    loadCounts();
  }, [tipoUnidad, filters]);

  const handleSearch = (value) => { setSearchTerm(value); setCurrentPage(1); };
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setSortField(field); setSortOrder('ASC'); }
    setCurrentPage(1);
  };

  const handleFilterChange = (filterKey, value) => { setFilters(prev => ({ ...prev, [filterKey]: value })); setCurrentPage(1); };
  const clearFilters = () => { setFilters({ estatus: '', empresa: '', base: '' }); setSearchTerm(''); setCurrentPage(1); };

  const handleTruckClick = (truck) => { navigate(`/trucks/${truck.id}`); };
  const handleAddTruck = () => { navigate('/trucks/new'); };

  const getStatusColor = (estatus) => {
    switch (estatus) {
      case 'Activo': return 'bg-green-100 text-green-800 border-green-200';
      case 'Mantenimiento preventivo': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Mantenimiento correctivo': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'En mantenimiento': return 'bg-yellow-100 text-yellow-800 border-yellow-200'; // Mantener compatibilidad con registros antiguos
      case 'Baja': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCompanyColor = (empresa) => {
    switch (empresa) {
      case 'TITSA': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TGT': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'GTT': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading && trucks.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <motion.div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} />
          <p className="text-xl text-gray-600">Cargando {title.toLowerCase()}...</p>
        </div>
      </div>
    );
  }

  // Helper: select image and alt text based on tipoUnidad
  const headerImageForTipo = (tipo) => {
    if (!tipo) return { src: '/images/tractocamion.png', alt: 'Flotilla' };
    const key = String(tipo).toLowerCase();
    if (key.includes('dolly')) return { src: '/images/dolly.png', alt: 'Dollys' };
  if (key.includes('auto') && key.includes('tanque')) return { src: '/images/auto_tanque.png', alt: 'auto tanque' };
  if (key.includes('caja')) return { src: '/images/caja_seca.png', alt: 'Caja Seca' };
  if (key.includes('unidad') && key.includes('gas')) return { src: '/images/unidades_gas.png', alt: 'unidad de gasolina' };
    if (key.includes('semi') || key.includes('remolque')) return { src: '/images/semiremolque.png', alt: 'Semiremolques' };
    if (key.includes('grua') || key.includes('gruas')) return { src: '/images/grua.png', alt: 'Gruas' };
    if (key.includes('tractocamion') || key.includes('tracto')) return { src: '/images/tractocamion.png', alt: 'Tractocamion' };
    return { src: '/images/tractocamion.png', alt: 'Flotilla' };
  };

  const headerImage = headerImageForTipo(tipoUnidad);

  // Calcular total agregado (Activo + Preventivo + Correctivo) para mostrar en encabezado
  const aggregatedOperationalTotal = (counts.activo || 0) + (counts.mantenimientoPreventivo || 0) + (counts.mantenimientoCorrectivo || 0);
  const isTractoPage = tipoUnidad && String(tipoUnidad).toLowerCase().includes('tracto');
  const isAutoTanquePage = tipoUnidad && (/autotanque/.test(String(tipoUnidad).toLowerCase()) || (String(tipoUnidad).toLowerCase().includes('auto') && String(tipoUnidad).toLowerCase().includes('tanque')));
  // Mostrar total agregado para cualquier página con tipoUnidad específico
  const useAggregatedHeader = Boolean(tipoUnidad);
  const headerTotalToShow = useAggregatedHeader ? aggregatedOperationalTotal : totalItems;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div className="flex items-center justify-between mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3">
            <img src={headerImage.src} alt={headerImage.alt} className="w-8 h-8 object-contain text-orange-500" />
            <h1 className="text-3xl font-bold text-gray-800">{title}</h1>
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
              {loadingCounts && useAggregatedHeader ? '...' : headerTotalToShow} unidades
            </span>
            <div className="ml-4 flex items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-green-50 text-green-800 border border-green-200">
                  {loadingCounts ? '...' : counts.activo}
                  <span className="ml-2 text-xs text-gray-600">Activo</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
                  {loadingCounts ? '...' : counts.mantenimientoPreventivo}
                  <span className="ml-2 text-xs text-gray-600">M. Preventivo</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-orange-50 text-orange-800 border border-orange-200">
                  {loadingCounts ? '...' : counts.mantenimientoCorrectivo}
                  <span className="ml-2 text-xs text-gray-600">M. Correctivo</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-red-50 text-red-800 border border-red-200">
                  {loadingCounts ? '...' : counts.baja}
                  <span className="ml-2 text-xs text-gray-600">Baja</span>
                </span>
              </div>
            </div>
          </div>

          <motion.button onClick={handleAddTruck} className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors shadow-lg" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Plus className="w-5 h-5" />
            Agregar Unidad
          </motion.button>
        </motion.div>

        <motion.div className="bg-white rounded-2xl shadow-xl p-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder={`Buscar por número económico, placas, número de serie (VIN), marca...`} 
                value={searchTerm} 
                onChange={(e) => handleSearch(e.target.value)} 
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" 
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <span className="text-xs text-gray-400">Busca por: económico, placas, VIN</span>
              </div>
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-colors ${showFilters ? 'bg-orange-100 text-orange-800 border-orange-300' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'}`}>
              <Filter className="w-5 h-5" />
              Filtros
            </button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estatus</label>
                  <select value={filters.estatus} onChange={(e) => handleFilterChange('estatus', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="">Todos los estatus</option>
                    {trucksService.getEstatusOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                  <select value={filters.empresa} onChange={(e) => handleFilterChange('empresa', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="">Todas las empresas</option>
                    {trucksService.getEmpresaOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Base</label>
                  <select value={filters.base} onChange={(e) => handleFilterChange('base', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="">Todas las bases</option>
                    {trucksService.getBaseOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3 flex justify-end">
                  <button onClick={clearFilters} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline">Limpiar filtros</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {error && (<motion.div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><p>Error: {error}</p></motion.div>)}

        <motion.div className="bg-white rounded-2xl shadow-xl overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('numero_economico')}>
                    <div className="flex items-center gap-1">Número Económico{sortField === 'numero_economico' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}</div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('placas')}>
                    Placas{sortField === 'placas' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('serie')}>
                    No. Serie (VIN){sortField === 'serie' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('marca')}>
                    Marca/Modelo{sortField === 'marca' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('empresa')}>
                    Empresa{sortField === 'empresa' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('base')}>
                    Base{sortField === 'base' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('estatus')}>
                    Estatus{sortField === 'estatus' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('tipo_unidad')}>
                    Tipo unidad{sortField === 'tipo_unidad' && (sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kilometraje</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trucks.map((truck, index) => (
                  <motion.tr key={truck.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => handleTruckClick(truck)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} whileHover={{ backgroundColor: '#f9fafb' }}>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="font-medium text-gray-900">{truck.numero_economico}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-gray-900">{truck.placas || 'N/A'}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-gray-900 font-mono text-sm">{truck.serie || 'N/A'}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-gray-900">{truck.marca}</div><div className="text-gray-500 text-sm">{truck.modelo}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getCompanyColor(truck.empresa)}`}><Building className="w-4 h-4 mr-1" />{truck.empresa}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className="inline-flex items-center text-gray-600"><MapPin className="w-4 h-4 mr-1" />{truck.base}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(truck.estatus)}`}><Activity className="w-4 h-4 mr-1" />{truck.estatus}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-gray-900">{truck.tipo_unidad || '—'}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-gray-900">{truck.km_actual?.toLocaleString() || '0'} km</div><div className="text-gray-500 text-sm">Próx: {truck.km_prox_servicio?.toLocaleString() || '0'} km</div></td>
                    <td className="px-6 py-4 whitespace-nowrap text-center"><button onClick={(e) => { e.stopPropagation(); handleTruckClick(truck); }} className="text-orange-600 hover:text-orange-900 p-2 rounded-lg hover:bg-orange-50" title="Ver detalles"><Eye className="w-5 h-5" /></button></td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">Mostrando {((currentPage - 1) * 30) + 1} - {Math.min(currentPage * 30, totalItems)} de {totalItems} unidades</div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft className="w-5 h-5" /></button>
                <span className="px-4 py-2 text-sm">Página {currentPage} de {totalPages}</span>
                <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="w-5 h-5" /></button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default UnitsPage;